import { normalizeSocialPrefill } from '~/utils/social-prefill';
import { clearApiCache } from '~/composables/useLocalizedFetch';

export type SocialProvider = 'google' | 'facebook' | 'apple';

export type SocialAuthSession = Record<string, any> & {
  email?: string;
  token?: string;
};

export type SocialAuthPrefill = {
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  avatar?: string;
  phone?: string;
  gender?: string;
  provider?: string;
};

export type SocialPendingRegistration = {
  pending_token: string;
  provider?: string;
  prefill: SocialAuthPrefill;
};

const DEFAULT_PROVIDERS: SocialProvider[] = [
  'google',
  'apple'
  // Facebook remains opt-in through the existing runtime configuration.
];

const trimTrailingSlash = (value: string) =>
  String(value || '').replace(/\/+$/, '');

const normalizeProvider = (provider: string): SocialProvider => {
  const normalized = String(provider || '')
    .toLowerCase()
    .trim();
  if (['google', 'facebook', 'apple'].includes(normalized)) {
    return normalized as SocialProvider;
  }
  throw new Error(`Unsupported social provider: ${provider}`);
};

export const useSocialProviders = () => {
  const config = useRuntimeConfig();
  const configured = (config.public as any).SOCIAL_LOGIN_PROVIDERS;

  if (Array.isArray(configured) && configured.length > 0) {
    return configured.map(normalizeProvider);
  }

  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.split(',').map(normalizeProvider);
  }

  return DEFAULT_PROVIDERS;
};

export const socialProviderLabel = (provider: string) => {
  const p = normalizeProvider(provider);
  return p.charAt(0).toUpperCase() + p.slice(1);
};

export const getSocialCallbackUrl = (params: Record<string, string> = {}) => {
  const config = useRuntimeConfig();
  const frontendUrl = trimTrailingSlash(
    String(config.public.FRONTEND_URL || '')
  );
  const locale = normalizeSocialLocale(params.lng || getCurrentLocaleSafely());
  const url = new URL(`${frontendUrl}${getSocialCallbackPath(locale)}`);

  url.searchParams.set('lng', locale);

  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }

  return url.toString();
};

const SUPPORTED_SOCIAL_LOCALES = new Set([
  'en',
  'tr',
  'de',
  'fr',
  'es',
  'pt',
  'it',
  'ar',
  'hi',
  'ur',
  'ru',
  'ja',
  'zh'
]);

const GOOGLE_LOCALE_MAP: Record<string, string> = {
  zh: 'zh-CN'
};

const normalizeSocialLocale = (value: unknown) => {
  const locale = String(value || '')
    .toLowerCase()
    .replace('_', '-')
    .trim();

  const language = locale.split('-')[0];
  return SUPPORTED_SOCIAL_LOCALES.has(language) ? language : 'en';
};

const googleLocale = (locale: string) => {
  const normalized = normalizeSocialLocale(locale);
  return GOOGLE_LOCALE_MAP[normalized] || normalized;
};

const getCurrentLocaleSafely = () => {
  try {
    const nuxtApp = useNuxtApp();
    const locale = nuxtApp.$i18n?.locale;
    const value = typeof locale === 'string' ? locale : locale?.value;
    if (value) return normalizeSocialLocale(value);
  } catch (_) {}

  try {
    const route = useRoute();
    const queryLocale = route.query?.lng || route.query?.locale;
    if (queryLocale) return normalizeSocialLocale(queryLocale);

    const firstSegment = route.path.split('/')[1];
    if (firstSegment) return normalizeSocialLocale(firstSegment);
  } catch (_) {}

  return 'en';
};

const getSocialCallbackPath = (locale: string) => {
  const normalized = normalizeSocialLocale(locale);
  return normalized === 'en'
    ? '/social-auth/callback'
    : `/${normalized}/social-auth/callback`;
};

const localizeProviderUrl = (provider: string, url: string, locale: string) => {
  if (normalizeProvider(provider) !== 'google') return url;

  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('google.')) return url;

    parsed.searchParams.set('hl', googleLocale(locale));
    return parsed.toString();
  } catch (_) {
    return url;
  }
};

export const buildSocialRedirectUrl = (
  provider: string,
  callbackParams: Record<string, string> = {}
) => {
  const p = normalizeProvider(provider);
  const config = useRuntimeConfig();
  const apiUrl = trimTrailingSlash(String(config.public.API_URL || ''));
  const pathTemplate = String(
    (config.public as any).SOCIAL_LOGIN_REDIRECT_PATH_TEMPLATE ||
      '/api/auth/social/{provider}/redirect'
  );
  const redirectPath = pathTemplate.replace(
    '{provider}',
    encodeURIComponent(p)
  );
  const locale = getCurrentLocaleSafely();
  const callbackUrl = getSocialCallbackUrl({
    popup: '1',
    ...callbackParams,
    lng: locale,
    ...(p === 'apple' ? { provider: p } : {})
  });

  const params = new URLSearchParams({
    redirect_url: callbackUrl,
    callback_url: callbackUrl,
    lng: locale,
    locale,
    hl: locale
  });

  return `${apiUrl}${redirectPath.startsWith('/') ? '' : '/'}${redirectPath}?${params.toString()}`;
};

const resolveSocialProviderUrl = async (
  provider: string,
  callbackParams: Record<string, string>
) => {
  const response = await $fetch<any>(
    buildSocialRedirectUrl(provider, callbackParams),
    {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      retry: 0,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }
  );

  if (typeof response === 'string') return response;
  if (response?.success === true && response?.url) return String(response.url);
  if (response?.url) return String(response.url);

  throw new Error(response?.message || 'socialRedirectUrlMissing');
};

export const SOCIAL_AUTH_CHANNEL = 'idmanify-social-auth-callback';
export const SOCIAL_AUTH_STORAGE_KEY = 'idmanify:social-auth-callback';

export const normalizeSocialCallbackUrlForMainWindow = (url: string) => {
  const normalized = new URL(url, window.location.origin);
  normalized.searchParams.delete('popup');
  return normalized.toString();
};

// Browser-only lifecycle: no durable OAuth-code or pending-token cache.
let cancelActiveSocialLogin: (() => void) | null = null;
let lastExchange: { code: string; promise: Promise<any> } | null = null;

export const cancelSocialLogin = () => {
  if (!import.meta.client) return;
  cancelActiveSocialLogin?.();
  lastExchange = null;
};

export const startSocialLogin = async (provider: string) => {
  if (!import.meta.client) return;
  cancelSocialLogin();

  const attempt = window.crypto.randomUUID();
  const locale = getCurrentLocaleSafely();
  const expectedCallback = new URL(getSocialCallbackUrl({ lng: locale }));
  let channel: BroadcastChannel | null = null;
  let finished = false;
  // Open synchronously from the click; Safari can otherwise block the popup.
  const popup = window.open('', '_blank', 'width=520,height=720');
  const cleanup = () => {
    finished = true;
    channel?.close();
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('pagehide', cleanup);
    if (cancelActiveSocialLogin === cleanup) cancelActiveSocialLogin = null;
    try {
      if (popup && !popup.closed) popup.close();
    } catch (_) {}
  };

  const continueInMainWindow = (payload: any) => {
    if (finished || payload?.attempt !== attempt || !payload?.url) return;
    try {
      const target = new URL(String(payload.url));
      if (
        target.origin !== expectedCallback.origin ||
        target.pathname !== expectedCallback.pathname ||
        target.searchParams.get('attempt') !== attempt
      )
        return;
      cleanup();
      // The login page must not remain as a replayable popup callback in history.
      window.location.replace(
        normalizeSocialCallbackUrlForMainWindow(target.toString())
      );
    } catch (_) {}
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== SOCIAL_AUTH_STORAGE_KEY || !event.newValue) return;
    try {
      continueInMainWindow(JSON.parse(event.newValue));
    } catch (_) {}
  };

  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel(`${SOCIAL_AUTH_CHANNEL}:${attempt}`);
    channel.onmessage = (event) => continueInMainWindow(event.data);
  }
  cancelActiveSocialLogin = cleanup;
  window.addEventListener('storage', onStorage);
  window.addEventListener('pagehide', cleanup);

  try {
    const providerUrl = localizeProviderUrl(
      provider,
      await resolveSocialProviderUrl(provider, {
        attempt,
        // A blocked popup uses the same redirect/exchange in the current tab.
        popup: popup ? '1' : '0'
      }),
      locale
    );
    if (finished) return; // A newer click/logout superseded this response.
    if (popup) {
      if (popup.closed) {
        cleanup();
        return;
      }
      popup.location.href = providerUrl;
    } else {
      cleanup();
      window.location.assign(providerUrl);
    }
  } catch (error) {
    const superseded = finished;
    cleanup();
    if (!superseded) throw error;
  }
};

const closeSocialPopup = () => {
  try {
    document.body.innerHTML =
      '<main style="font-family:system-ui,sans-serif;display:grid;place-items:center;min-height:100vh;color:#111;text-align:center;padding:24px"><div><strong>Login wird im Hauptfenster fortgesetzt…</strong><br><span style="color:#666;font-size:14px">Dieses Fenster schließt sich automatisch.</span></div></main>';
  } catch (_) {}

  const close = () => {
    try {
      window.close();
    } catch (_) {}
  };

  close();
  window.setTimeout(close, 100);
  window.setTimeout(close, 350);
  window.setTimeout(() => {
    try {
      window.open('', '_self');
      window.close();
    } catch (_) {}
  }, 700);
};

export const handOffSocialCallbackToMainWindow = () => {
  if (!import.meta.client) return false;

  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get('popup') !== '1') return false;

  const targetUrl = normalizeSocialCallbackUrlForMainWindow(
    currentUrl.toString()
  );
  const attempt = currentUrl.searchParams.get('attempt') || '';
  const payload = { url: targetUrl, attempt, at: Date.now() };

  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(
      attempt ? `${SOCIAL_AUTH_CHANNEL}:${attempt}` : SOCIAL_AUTH_CHANNEL
    );
    channel.postMessage(payload);
    channel.close();
  }

  // Compatibility fallback when BroadcastChannel is unavailable. Remove the
  // transient handoff immediately: do not retain one-use codes in localStorage.
  try {
    localStorage.setItem(SOCIAL_AUTH_STORAGE_KEY, JSON.stringify(payload));
    localStorage.removeItem(SOCIAL_AUTH_STORAGE_KEY);
  } catch (_) {}
  closeSocialPopup();

  return true;
};

const pickFirst = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const safeJsonParse = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return null;

  const attempts = [value];
  try {
    attempts.push(decodeURIComponent(value));
  } catch (_) {}
  try {
    attempts.push(atob(value));
  } catch (_) {}

  for (const attempt of attempts) {
    try {
      return JSON.parse(attempt);
    } catch (_) {}
  }

  return null;
};

export const unwrapSocialResponse = (raw: any) => {
  const parsedPayload = safeJsonParse(
    raw?.payload || raw?.social_payload || raw?.data
  );
  const source = parsedPayload || raw || {};
  const data =
    source?.data && typeof source.data === 'object' ? source.data : source;
  const user =
    data?.user || data?.client || data?.auth || data?.session || data;
  const prefill =
    data?.prefill ||
    data?.provider_data ||
    data?.social_user ||
    data?.social ||
    {};

  const pendingToken = pickFirst(
    data?.pending_token,
    data?.pendingToken,
    source?.pending_token,
    source?.pendingToken
  );

  if (pendingToken) {
    return {
      status: 'pending' as const,
      pending: {
        pending_token: pendingToken,
        provider: pickFirst(
          data?.provider,
          prefill?.provider,
          source?.provider
        ),
        prefill: normalizeSocialPrefill(
          prefill,
          data?.provider_data,
          data?.social_user,
          data?.social,
          data?.user,
          data,
          source
        )
      } satisfies SocialPendingRegistration
    };
  }

  const email = pickFirst(user?.email, data?.email, source?.email);

  if (email) {
    return {
      status: 'authenticated' as const,
      session: { ...user, ...data, email } as SocialAuthSession
    };
  }

  return {
    status: 'error' as const,
    message: pickFirst(data?.message, source?.message, source?.error)
  };
};

export const persistSocialSession = async (session: SocialAuthSession) => {
  if (!session?.email) return false;

  const nuxtApp = useNuxtApp();
  const auth = useAuth();
  const safeSession = { ...session };
  delete safeSession.token;
  delete safeSession.access_token;
  delete safeSession.plain_text_token;
  delete safeSession.identity_token;
  delete safeSession.id_token;
  delete safeSession.refresh_token;

  auth.session.value = safeSession;

  useCookie('idmanify_user', {
    path: '/',
    sameSite: 'strict',
    maxAge: 60 * 60 * 24 * 365
  }).value = safeSession;

  await auth.fetchUser();
  clearApiCache(nuxtApp);
  return auth.loggedIn.value;
};

const csrfCookie = async (apiUrl: string) => {
  await $fetch(`${apiUrl}/api/auth/csrf-cookie`, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store'
  });

  // After logout Laravel may rotate/expire the XSRF cookie. Nuxt's useCookie
  // value can otherwise still hold the stale token until a full reload.
  if (import.meta.client) refreshCookie('XSRF-TOKEN');
};

const xsrfHeader = () => {
  const token = useCookie<string>('XSRF-TOKEN').value;
  return token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {};
};

const postSocialJson = async (endpoint: string, body: Record<string, any>) => {
  const config = useRuntimeConfig();
  const apiUrl = trimTrailingSlash(String(config.public.API_URL || ''));

  await csrfCookie(apiUrl);

  return await $fetch(
    `${apiUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`,
    {
      method: 'POST',
      retry: 0,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...xsrfHeader()
      },
      body
    }
  );
};

export const exchangeSocialCode = async (code: string) => {
  const config = useRuntimeConfig();
  const endpoint = String(
    (config.public as any).SOCIAL_LOGIN_EXCHANGE_PATH ||
      '/api/auth/social/exchange'
  );

  // A component remount/concurrent resolve must share the first exchange,
  // including a failed response. One-use codes must never be retried implicitly.
  if (import.meta.client && lastExchange?.code === code)
    return lastExchange.promise;
  const promise = postSocialJson(endpoint, { code });
  if (import.meta.client) lastExchange = { code, promise };
  return await promise;
};

export const completeSocialRegistration = async (
  payload: Record<string, any>
) => {
  const body = { ...payload };
  body.lng = getCurrentLocaleSafely();

  const endpointCandidates = [
    '/api/auth/social/complete-registration',
    '/api/auth/social/register',
    '/api/auth/social/complete'
  ];

  let lastError: any = null;

  for (const endpoint of endpointCandidates) {
    try {
      return await postSocialJson(endpoint, body);
    } catch (error: any) {
      lastError = error;
      if (
        ![404, 405].includes(
          Number(error?.statusCode || error?.response?.status)
        )
      ) {
        throw error;
      }
    }
  }

  throw lastError;
};

export const extractValidationMessage = (
  error: any,
  fallback: string,
  provider?: string
) => {
  const data = error?.data || error?.response?._data || error;
  let details = '';

  if (data?.errors && typeof data.errors === 'object') {
    for (const value of Object.values<any>(data.errors)) {
      if (Array.isArray(value) && value[0]) details += `${value[0]}\n`;
      else if (typeof value === 'string') details += `${value}\n`;
    }
  }

  // Keep actionable form validation. Apple OAuth/JWT/configuration codes and
  // callback descriptions are not user copy; use the caller's existing locale.
  if (details.trim()) return details.trim();
  if (
    provider === 'apple' ||
    ['invalidOrExpiredCode', 'invalid_or_expired_code'].includes(
      data?.message || data?.error
    )
  )
    return fallback;
  return data?.message || data?.error || fallback;
};
